import { PButton, type PButtonProps, PButtonPure, componentsReady } from '@porsche-design-system/components-react';
import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { type ButtonProps } from '../context';

const BUTTON_TAG = 'p-button';

const StyledPButton = styled(PButton)<{ block?: boolean }>`
    width: ${({ block }) => (block ? '100%' : 'auto')};
`;

const StyledPButtonPure = styled(PButtonPure)<{ block?: boolean }>`
    width: ${({ block }) => (block ? '100%' : 'auto')};
`;

type AntdButtonTypes = ButtonProps['type'];

const getPorscheButtonVariant = (type: AntdButtonTypes): PButtonProps['variant'] => {
    switch (type) {
        case 'primary':
            return 'primary';

        case 'default':
            return 'secondary';

        default:
            return 'secondary';
    }
};

const getLoading = (loading: ButtonProps['loading']) => {
    if (typeof loading === 'boolean') {
        return loading;
    }

    return !!loading;
};

const appendClonedSubmitButton = (wrapper: HTMLDivElement, form: string) => {
    // note: select the button element inside the shadow dom
    const pButtonElement = wrapper.querySelector(BUTTON_TAG) as HTMLDivElement;
    const shadowRoot = pButtonElement?.shadowRoot;
    const button = shadowRoot?.querySelector('button');

    if (!button) {
        return;
    }

    // note: clone the button and append it to the wrapper, and hide it
    const clonedButton = button?.cloneNode(true) as HTMLButtonElement;
    clonedButton.setAttribute('hidden', 'true');
    clonedButton.setAttribute('form', form);

    pButtonElement.appendChild(clonedButton);

    // note: trigger the click event on the cloned button when the original button is clicked
    button.addEventListener('click', () => clonedButton.click());
};

const Button = ({
    children,
    type,
    porscheFallbackIcon = 'none',
    porscheFallbackIconSource,
    htmlType = 'button',
    value,
    disabled,
    onClick,
    className,
    form,
    style,
    loading,
    ...props
}: ButtonProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!wrapperRef.current || !form || htmlType !== 'submit') {
            return;
        }

        const wrapper = wrapperRef.current;
        componentsReady(wrapper).then(() => appendClonedSubmitButton(wrapper, form));
    }, [form, htmlType]);

    const childNode = useMemo(() => {
        if (type === 'link') {
            return (
                <StyledPButtonPure
                    block={props.block}
                    disabled={disabled}
                    icon={porscheFallbackIcon}
                    iconSource={porscheFallbackIconSource}
                    loading={getLoading(loading)}
                    onClick={onClick}
                    style={style}
                    type={htmlType}
                    value={value ? value.toString() : undefined}
                >
                    <span style={{ wordBreak: 'keep-all' }}>{children}</span>
                </StyledPButtonPure>
            );
        }

        return (
            <StyledPButton
                {...props}
                disabled={disabled}
                hideLabel={!children}
                icon={porscheFallbackIcon}
                iconSource={porscheFallbackIconSource}
                loading={getLoading(loading)}
                name={form}
                onClick={onClick}
                style={style}
                type={htmlType}
                value={value ? value.toString() : undefined}
                variant={getPorscheButtonVariant(type)}
            >
                {children}
            </StyledPButton>
        );
    }, [
        children,
        disabled,
        form,
        htmlType,
        loading,
        onClick,
        porscheFallbackIcon,
        porscheFallbackIconSource,
        props,
        style,
        type,
        value,
    ]);

    return (
        <div ref={wrapperRef} className={`${BUTTON_TAG} ${className || ''}`}>
            {childNode}
        </div>
    );
};

export default Button;
